import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import {
  ArrowLeft, ShieldCheck, Lock, CheckCircle2, Info, CreditCard, Landmark, Wallet, Building2,
} from "lucide-react";
import { api } from "../lib/api";
import { useEntitlements, invalidateEntitlements } from "../lib/entitlements";
import Logo from "../components/Logo";
import LegalFooter from "../components/LegalFooter";

/**
 * Native Mi Contador checkout — 2-panel review screen.
 *
 * LEFT panel : polished order summary using the existing Mi Contador design
 *              system (logo, plan name, billing frequency, features, price).
 * RIGHT panel: Mercado Pago handoff — trust badges, list of supported payment
 *              methods (card / SPEI / OXXO / MP Wallet), primary "Pagar con
 *              Mercado Pago" button that calls our backend to create a
 *              preapproval or preference, then redirects to MP's hosted
 *              checkout (`init_point`). Mercado Pago handles the sensitive
 *              card/SPEI/OXXO capture — we never touch raw card data.
 *
 * Route: /checkout?plan=empresa&type=monthly_recurring
 * After MP confirms via webhook, /checkout/success activates the plan.
 */
const _PAYMENT_METHODS = [
  { icon: CreditCard, label: "Tarjeta de crédito o débito" },
  { icon: Wallet, label: "Saldo de Mercado Pago" },
  { icon: Building2, label: "SPEI (transferencia)" },
  { icon: Landmark, label: "Efectivo en OXXO" },
];

export default function CheckoutPage() {
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const planId = (search.get("plan") || "").toLowerCase();
  const paymentType = (search.get("type") || "monthly_recurring").toLowerCase();

  const { status, refresh } = useEntitlements();
  const [variant, setVariant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [promoCode, setPromoCode] = useState("");
  const [promoBusy, setPromoBusy] = useState(false);
  const [promoError, setPromoError] = useState("");
  const [appliedPromo, setAppliedPromo] = useState(null); // { code, discount_percent, label }

  useEffect(() => {
    // Any user hitting checkout should NOT already be on this exact plan.
    if (status?.is_paid && status?.plan === planId) {
      navigate("/mi-suscripcion", { replace: true });
    }
  }, [status, planId, navigate]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/payments/catalog");
        if (cancelled) return;
        const found = (r.data?.variants || []).find(
          (v) => v.plan_id === planId && v.payment_type === paymentType,
        );
        setVariant(found || null);
      } catch (_e) { setVariant(null); }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [planId, paymentType]);

  const startPayment = async () => {
    if (!variant) return;
    setBusy(true); setError("");
    try {
      const r = await api.post("/payments/checkout",
        { plan_id: planId, payment_type: paymentType },
        { withCredentials: true });
      if (r.data?.checkout_url) {
        window.location.assign(r.data.checkout_url);
        return;
      }
      setError("No pudimos iniciar el checkout con Mercado Pago.");
    } catch (e) {
      // 503 = MP not configured yet. Fall back to the legacy immediate
      // activation so demo/promo flows keep working.
      if (e.response?.status === 503) {
        try {
          const legacy = await api.post("/subscription/select-plan",
            { plan: planId }, { withCredentials: true });
          if (legacy.data?.plan === planId) {
            invalidateEntitlements(); await refresh();
            navigate(`/checkout/success?ref=demo-${planId}`, { replace: true });
            return;
          }
        } catch (_ignore) { /* fallthrough */ }
      }
      setError(e.response?.data?.detail || "No pudimos iniciar el pago.");
    } finally { setBusy(false); }
  };

  useEffect(() => {
    // If the user already redeemed a code (e.g. via profile), pre-fill it so
    // the discount is visible on the checkout summary — no double-hunt.
    if (status?.promo_code && status?.promo_discount && !appliedPromo) {
      setAppliedPromo({
        code: status.promo_code,
        discount_percent: status.promo_discount,
        label: `${status.promo_discount}% de descuento`,
      });
    }
  }, [status, appliedPromo]);

  const redeemPromo = async () => {
    const code = (promoCode || "").trim().toUpperCase();
    if (!code) return;
    setPromoBusy(true); setPromoError("");
    try {
      const r = await api.post("/subscription/redeem-promo",
        { code }, { withCredentials: true });
      // Backend returns the updated compute_status snapshot + `promo_applied`.
      setAppliedPromo({
        code: r.data?.promo_code || code,
        discount_percent: r.data?.promo_discount || 0,
        label: r.data?.promo_applied || `${r.data?.promo_discount || 0}% de descuento`,
      });
      setPromoCode("");
      invalidateEntitlements(); refresh();
    } catch (e) {
      // 409 = already redeemed; surface a friendlier message for that case.
      const status = e.response?.status;
      if (status === 409) setPromoError("Ya tienes un código aplicado en tu cuenta.");
      else setPromoError(e.response?.data?.detail || "No pudimos aplicar el código.");
    } finally { setPromoBusy(false); }
  };

  // Discounted total that mirrors what the backend will charge — the user
  // sees the promo applied before they hand off to Mercado Pago.
  const discountPct = appliedPromo?.discount_percent || 0;
  const discountedAmount = variant
    ? Math.round(variant.amount * (1 - discountPct / 100))
    : 0;
  const discountValue = variant ? variant.amount - discountedAmount : 0;

  const isYearly = paymentType.startsWith("yearly_");
  const isManual = paymentType.endsWith("_manual");

  if (loading) {
    return (
      <div className="checkout-shell" data-testid="checkout-loading">
        <div className="checkout-loading-card">
          <Logo variant="full" size="md" />
          <span>Preparando tu checkout…</span>
        </div>
      </div>
    );
  }
  if (!variant) {
    return (
      <div className="checkout-shell">
        <div className="checkout-loading-card" data-testid="checkout-invalid-plan">
          <Logo variant="full" size="md" />
          <h2 style={{ font: '900 22px "Nunito", sans-serif', color: "#0f172a", margin: "10px 0 6px" }}>Plan no encontrado</h2>
          <p style={{ color: "#64748b", font: '500 14px "Nunito", sans-serif' }}>
            No pudimos cargar el plan seleccionado. Elige uno desde la página de precios.
          </p>
          <Link to="/pricing" className="pricing-cta primary" style={{ marginTop: 12 }}>Ver planes</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="checkout-shell" data-testid="checkout-page">
      <header className="checkout-topbar">
        <Link to="/pricing" className="checkout-back" data-testid="checkout-back-link">
          <ArrowLeft size={16} /> Cambiar de plan
        </Link>
        <Logo variant="full" size="md" testid="checkout-logo" />
        <div className="checkout-secure-tag" title="Pago cifrado y procesado por Mercado Pago">
          <Lock size={12} /> Pago 100% seguro
        </div>
      </header>

      <main className="checkout-grid">
        {/* ── LEFT: Mi Contador order summary ─────────────────────────── */}
        <section className="checkout-panel checkout-summary" data-testid="checkout-summary">
          <small className="eyebrow">TU PLAN</small>
          <h1 data-testid="checkout-plan-name">{variant.plan_name}</h1>
          <p className="checkout-tagline">
            {isManual
              ? (isYearly ? "Pago único · 12 meses de acceso" : "Pago único · 30 días de acceso")
              : (isYearly ? "Renovación automática anual" : "Renovación automática mensual")}
          </p>

          <div className="checkout-price-card" data-testid="checkout-price-card">
            <div className="checkout-price-row">
              <span>{isYearly ? "Precio anual" : "Precio mensual"}</span>
              <b>${variant.amount.toLocaleString("es-MX")} {variant.currency}</b>
            </div>
            {isYearly && (
              <>
                <div className="checkout-price-row" style={{ color: "#64748b" }}>
                  <span>Precio sin descuento</span>
                  <s>${(variant.monthly_full_price * 12).toLocaleString("es-MX")} MXN</s>
                </div>
                <div className="checkout-price-row" style={{ color: "#059669" }}>
                  <span>2 meses gratis</span>
                  <b>− ${variant.savings.toLocaleString("es-MX")} MXN</b>
                </div>
              </>
            )}
            {appliedPromo && discountPct > 0 && (
              <div className="checkout-price-row" style={{ color: "#059669" }} data-testid="checkout-promo-row">
                <span>Código {appliedPromo.code}</span>
                <b>− ${discountValue.toLocaleString("es-MX")} MXN</b>
              </div>
            )}
            <div className="checkout-price-divider" />
            <div className="checkout-price-row total">
              <span>Total a pagar hoy</span>
              <b data-testid="checkout-total">${discountedAmount.toLocaleString("es-MX")} MXN</b>
            </div>
            {isYearly && (
              <div className="checkout-monthly-equiv">
                Equivale a ${Math.round(discountedAmount / 12).toLocaleString("es-MX")} MXN/mes
              </div>
            )}
          </div>

          <div className="checkout-promo" data-testid="checkout-promo">
            {appliedPromo ? (
              <div className="checkout-promo-applied" data-testid="checkout-promo-applied">
                <CheckCircle2 size={14} color="#059669" />
                <span><b>{appliedPromo.code}</b> aplicado — {appliedPromo.label}</span>
              </div>
            ) : (
              <>
                <label htmlFor="promo-code-input" className="checkout-promo-label">
                  ¿Tienes un código promocional?
                </label>
                <div className="checkout-promo-row">
                  <input
                    id="promo-code-input"
                    type="text"
                    value={promoCode}
                    onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                    placeholder="Escribe tu código"
                    disabled={promoBusy}
                    data-testid="checkout-promo-input"
                    onKeyDown={(e) => e.key === "Enter" && redeemPromo()}
                  />
                  <button
                    type="button"
                    onClick={redeemPromo}
                    disabled={promoBusy || !promoCode.trim()}
                    data-testid="checkout-promo-apply"
                  >
                    {promoBusy ? "…" : "Aplicar"}
                  </button>
                </div>
                {promoError && (
                  <div className="checkout-promo-error" data-testid="checkout-promo-error">
                    {promoError}
                  </div>
                )}
              </>
            )}
          </div>

          <h3 className="checkout-features-title">Incluye:</h3>
          <ul className="checkout-features" data-testid="checkout-features">
            {variant.features.slice(0, 8).map((f) => (
              <li key={f}><CheckCircle2 size={14} color="#10b981" /> <span>{f}</span></li>
            ))}
          </ul>

          <div className="checkout-safety">
            <ShieldCheck size={14} /> Puedes cancelar cuando quieras. Sin cargos ocultos.
          </div>
        </section>

        {/* ── RIGHT: Mercado Pago handoff ─────────────────────────────── */}
        <section className="checkout-panel checkout-payment" data-testid="checkout-payment">
          <small className="eyebrow" style={{ color: "#4169e1" }}>PAGO SEGURO</small>
          <h2>Pagar con <span className="mp-word">Mercado Pago</span></h2>
          <p className="checkout-tagline">
            Al continuar, te llevaremos a Mercado Pago para completar tu pago
            de forma segura. No guardamos ni procesamos los datos de tu tarjeta.
          </p>

          <ul className="checkout-methods" data-testid="checkout-methods">
            {_PAYMENT_METHODS.map(({ icon: Icon, label }) => (
              <li key={label}>
                <span className="checkout-method-icon"><Icon size={16} /></span>
                <span>{label}</span>
              </li>
            ))}
          </ul>

          <button
            className="checkout-pay-btn"
            onClick={startPayment}
            disabled={busy}
            data-testid="checkout-pay-button"
          >
            {busy ? "Preparando el pago…" : "Pagar con Mercado Pago"}
          </button>
          {error && <div className="checkout-error" data-testid="checkout-error">{error}</div>}

          <div className="checkout-trust">
            <span><Lock size={12} /> SSL</span>
            <span><ShieldCheck size={12} /> PCI DSS · MP</span>
            <span><Info size={12} /> Sin cargos ocultos</span>
          </div>
          <p className="checkout-fine-print">
            Al pagar aceptas nuestros <Link to="/legal/terminos">Términos</Link> y{" "}
            <Link to="/legal/privacidad">Aviso de Privacidad</Link>.
            {" "}{isManual ? "No se realizará ningún cobro automático." : "Puedes cancelar la renovación cuando quieras."}
          </p>
        </section>
      </main>

      <LegalFooter variant="pricing" />
    </div>
  );
}
